import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError } from '../../../api';
import { useMe, useUsers } from '../../../hooks';
import { isManager } from '../../../shared/persona';
import {
  approvePublish,
  fetchAdmin,
  hideResume,
  rejectPublish,
  revokeUserGrant,
  rollbackResume,
  saveEligibility,
  unhideResume,
  type AdminData,
  type AdminProfile,
} from '../client';
import './console.css';

function errorText(error: unknown): string {
  if (error instanceof ApiError) return error.code;
  if (error instanceof Error) return error.message;
  return '操作失败';
}

function formatTime(value: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('zh-CN', { hour12: false });
}

const STATUS_LABEL: Record<string, string> = {
  draft: '草稿',
  published: '已上线',
  hidden: '已隐藏',
  pending: '待审核',
  rejected: '已驳回',
  superseded: '已被替代',
};

const ROLLBACKABLE = new Set(['published', 'superseded', 'hidden']);

export default function ResumeAdmin() {
  const me = useMe();
  const isAdmin = isManager(me.data?.persona ?? 'visitor');
  const users = useUsers(isAdmin);
  const admin = useQuery<AdminData>({
    queryKey: ['resume-admin'],
    queryFn: fetchAdmin,
    enabled: isAdmin,
  });
  const queryClient = useQueryClient();
  const [notice, setNotice] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [userId, setUserId] = useState<number | ''>('');
  const [slug, setSlug] = useState('');
  const [canPublish, setCanPublish] = useState(true);
  const [autoApprove, setAutoApprove] = useState(false);
  const [isCurrent, setIsCurrent] = useState(true);
  const [notes, setNotes] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!admin.data) return;
    if (userId === '') return;
    const profile = admin.data.profiles.find((item) => item.user_id === userId);
    if (profile) {
      setSlug(profile.slug);
      setCanPublish(profile.canPublish);
      setAutoApprove(profile.autoApprove);
      setIsCurrent(profile.isCurrent);
    }
  }, [admin.data, userId]);

  if (!isAdmin) {
    return (
      <div className="empty">
        <b>仅管理员/组内教师可见</b>
        当前账号没有简历审核权限。
      </div>
    );
  }

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['resume-admin'] });
  };

  const run = async (fn: () => Promise<unknown>, okText: string) => {
    setBusy(true);
    setNotice(null);
    try {
      await fn();
      setNotice({ kind: 'ok', text: okText });
      await refresh();
    } catch (error) {
      setNotice({ kind: 'err', text: errorText(error) });
    } finally {
      setBusy(false);
    }
  };

  const selectUser = (value: string) => {
    if (!value) {
      setUserId('');
      return;
    }
    const id = Number(value);
    setUserId(id);
    const profile = admin.data?.profiles.find((item) => item.user_id === id);
    setSlug(profile?.slug ?? `member-${id}`);
    setCanPublish(profile?.canPublish ?? true);
    setAutoApprove(profile?.autoApprove ?? false);
    setIsCurrent(profile?.isCurrent ?? true);
  };

  const save = () => {
    if (userId === '') {
      setNotice({ kind: 'err', text: '先选择一个账号。' });
      return;
    }
    void run(
      () => saveEligibility(userId, { slug, canPublish, autoApprove, isCurrent }),
      '资格已保存。',
    );
  };

  const profiles = admin.data?.profiles ?? [];
  const pending = admin.data?.pending ?? [];

  return (
    <div className="resume-console">
      <div className="page-head">
        <div>
          <div className="eyebrow">Admin</div>
          <h1>简历审核</h1>
        </div>
        <div className="resume-status-row">
          <span className="badge">{profiles.length} 位成员</span>
          <span className="badge warn">{pending.length} 个待审</span>
        </div>
      </div>

      {notice ? <div className={`notice ${notice.kind}`}>{notice.text}</div> : null}

      <section className="panel">
        <div className="section-head">
          <h2>发布资格</h2>
          <span className="muted">设置 slug、发布权和自动上线；首次发布后 slug 不可改</span>
        </div>
        <div className="resume-admin-form">
          <div className="field">
            <label htmlFor="resume-admin-user">账号</label>
            <select
              id="resume-admin-user"
              value={userId === '' ? '' : String(userId)}
              onChange={(event) => selectUser(event.target.value)}
            >
              <option value="">选择成员账号</option>
              {(users.data ?? []).map((user) => (
                <option key={user.id} value={user.id}>
                  {user.display_name}（{user.username}）
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor="resume-admin-slug">slug</label>
            <input
              id="resume-admin-slug"
              value={slug}
              maxLength={32}
              placeholder="zhang-san"
              onChange={(event) => setSlug(event.target.value)}
            />
          </div>
          <label className="resume-check">
            <input
              type="checkbox"
              checked={canPublish}
              onChange={(event) => setCanPublish(event.target.checked)}
            />
            可发布
          </label>
          <label className="resume-check">
            <input
              type="checkbox"
              checked={autoApprove}
              onChange={(event) => setAutoApprove(event.target.checked)}
            />
            首次审核后自动上线
          </label>
          <label className="resume-check">
            <input
              type="checkbox"
              checked={isCurrent}
              onChange={(event) => setIsCurrent(event.target.checked)}
            />
            在读成员
          </label>
          <button type="button" className="btn btn-primary" disabled={busy} onClick={save}>
            保存资格
          </button>
        </div>
      </section>

      <section className="panel">
        <div className="section-head">
          <h2>待审核</h2>
          <span className="muted">预览链接只对管理员会话有效</span>
        </div>
        {pending.length ? (
          <div className="resume-pending-list">
            {pending.map((row) => (
              <article className="resume-pending" key={row.id}>
                <div>
                  <h3>{row.manifest?.meta?.name ?? row.slug}</h3>
                  <p className="muted">
                    {row.slug} · {formatTime(row.created_at)}
                  </p>
                </div>
                <div className="resume-actions">
                  <a
                    className="btn btn-ghost btn-sm"
                    href={`/resume-preview/${row.id}/`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    预览
                  </a>
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    disabled={busy}
                    onClick={() => void run(() => approvePublish(row.id), '已通过并上线。')}
                  >
                    通过
                  </button>
                  <input
                    className="resume-note-input"
                    placeholder="驳回原因（可选）"
                    value={notes[row.id] ?? ''}
                    onChange={(event) => setNotes({ ...notes, [row.id]: event.target.value })}
                  />
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    disabled={busy}
                    onClick={() =>
                      void run(() => rejectPublish(row.id, notes[row.id] ?? ''), '已驳回。')
                    }
                  >
                    驳回
                  </button>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="empty">没有待审的简历包。</div>
        )}
      </section>

      <section className="panel">
        <div className="section-head">
          <h2>成员简历</h2>
          <span className="muted">下线会撤销发布权；恢复会回到最近隐藏的版本</span>
        </div>
        {profiles.length ? (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>成员</th>
                  <th>slug</th>
                  <th>状态</th>
                  <th>权限</th>
                  <th>版本</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {profiles.map((profile: AdminProfile) => (
                  <tr key={profile.user_id}>
                    <td>
                      <b>{profile.display_name}</b>
                      <div className="muted">{profile.isCurrent ? '在读' : '已毕业'}</div>
                    </td>
                    <td>{profile.slug}</td>
                    <td>{STATUS_LABEL[profile.status] ?? profile.status}</td>
                    <td>
                      {profile.canPublish ? '可发布' : '只读'}
                      {profile.autoApprove ? ' · 自动上线' : ''}
                    </td>
                    <td>
                      {profile.publishes.length ? (
                        <div className="resume-version-list">
                          {profile.publishes.slice(0, 4).map((publish) => (
                            <div key={publish.id} className="resume-version">
                              <span>{publish.id.slice(0, 8)}</span>
                              <span className="muted">{STATUS_LABEL[publish.status] ?? publish.status}</span>
                              {ROLLBACKABLE.has(publish.status) ? (
                                <button
                                  type="button"
                                  className="btn btn-ghost btn-sm"
                                  disabled={busy}
                                  onClick={() =>
                                    void run(
                                      () => rollbackResume(profile.user_id, publish.id),
                                      '已回滚到该版本。',
                                    )
                                  }
                                >
                                  回滚
                                </button>
                              ) : null}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                    <td>
                      <div className="resume-actions">
                        {profile.status === 'published' ? (
                          <a
                            className="btn btn-ghost btn-sm"
                            href={`/resume/${profile.slug}/`}
                            target="_blank"
                            rel="noreferrer"
                          >
                            线上
                          </a>
                        ) : null}
                        {profile.status === 'hidden' ? (
                          <button
                            type="button"
                            className="btn btn-ghost btn-sm"
                            disabled={busy}
                            onClick={() => void run(() => unhideResume(profile.user_id), '已恢复上线。')}
                          >
                            恢复
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="btn btn-ghost btn-sm"
                            disabled={busy}
                            onClick={() => {
                              if (window.confirm(`下线 ${profile.display_name} 的简历？`)) {
                                void run(() => hideResume(profile.user_id), '已下线。');
                              }
                            }}
                          >
                            下线
                          </button>
                        )}
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          disabled={busy}
                          onClick={() => void run(() => revokeUserGrant(profile.user_id), '发布权已撤销。')}
                        >
                          撤令牌
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="empty">还没有成员资料。先在上面选择账号并保存资格。</div>
        )}
      </section>
    </div>
  );
}